import React, { useEffect, useState } from 'react';
import { Row, Col, Spin, Button, Modal, Typography, theme } from 'antd';
import dayjs from 'dayjs';
import { message } from '../../services/toastMessage';
import { PlusOutlined } from '@ant-design/icons';
import { IconHomeOff, IconEngine, IconAlertTriangle } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import PageHeaderRow from '../../components/Layouts/PageHeaderRow';
import PayoutIssueBanner from '../../components/PayoutIssueBanner';
import {
  getMyHeavyDutyMachineries,
  deleteHeavyDutyMachinery,
  publishHeavyDutyMachinery,
  putMachineryBackInService,
} from '../../services/HeavyDutyMachineryService';
import MachineryOutOfServiceModal from '../../components/MachineryOutOfServiceModal';
import { IAffectedBooking, IHeavyDutyMachinery } from '../../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import HeavyDutyMachineryCard from './HeavyDutyMachineryCard';
import MySpacesEmptyStateMobile from './MySpacesEmptyStateMobile';

// Mobile only -- same pattern as MyOperators/MyTransportVehicles: everything
// on this page that reads token.colorPrimary (the "Añadir máquina" buttons,
// up top and in the empty state) reads coral, which is the app-wide primary
// now (see theme/appTheme) rather than something this page had to ask for.
// The wrapper + Content split is left as it was: it costs nothing and is
// what any future scoped theme here would need anyway.
const { Text } = Typography;

const MyHeavyDutyMachineries: React.FC = () => {
  const { isMobile } = useAppBreakpoint();
  return isMobile ? (
    <MyHeavyDutyMachineriesContent />
  ) : (
    <MyHeavyDutyMachineriesContent />
  );
};

const MyHeavyDutyMachineriesContent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const [spaces, setSpaces] = useState<IHeavyDutyMachinery[]>([]);
  const [loading, setLoading] = useState(true);
  // Distinct from spaces.length === 0 -- a failed fetch must never render
  // the same "add your first machine" empty state a host with a genuinely
  // empty fleet sees, or a real backend error looks identical to having no
  // machines at all.
  const [loadError, setLoadError] = useState(false);
  const [parkingSpace, setParkingSpace] = useState<{ id: string; title: string } | undefined>(undefined);
  // Shown AFTER the machine is parked, never instead of it. Taking it off
  // the market is done; these are what the host still has to sort out by
  // hand, and burying them in a toast would be the same as not saying it.
  const [affected, setAffected] = useState<IAffectedBooking[] | undefined>(undefined);

  const load = () => {
    setLoading(true);
    setLoadError(false);
    getMyHeavyDutyMachineries()
      .then(setSpaces)
      .catch(() => {
        setLoadError(true);
        message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_LOAD_ERROR));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id: string) => {
    try {
      await deleteHeavyDutyMachinery(id);
      setSpaces((prev) => prev.filter((s) => s.id !== id));
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_DELETE_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_DELETE_ERROR));
    }
  };

  const handlePublish = async (id: string) => {
    try {
      const updated = await publishHeavyDutyMachinery(id);
      setSpaces((prev) => prev.map((s) => (s.id === id ? updated : s)));
      // Manual-approval mode (see AdminSpacesController/PlatformSettings)
      // parks the space in PendingVerification instead of going live
      // straight away -- a different message than the usual "published".
      message.success(
        translate(t, updated.status === 'PendingVerification' ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_PUBLISH_PENDING_REVIEW : TRANSLATIONS.HEAVY_DUTY_MACHINERY_PUBLISH_SUCCESS)
      );
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_PUBLISH_ERROR));
    }
  };

  const handleBackInService = async (id: string) => {
    try {
      const updated = await putMachineryBackInService(id);
      setSpaces((prev) => prev.map((s) => (s.id === id ? updated : s)));
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BACK_IN_SERVICE_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BACK_IN_SERVICE_ERROR));
    }
  };

  const hasPayoutIssue = spaces.some((s) => s.status === 'Suspended' && s.suspensionReason === 'StripePayoutsDisabled');

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 1100 }}>
        {hasPayoutIssue && <PayoutIssueBanner />}
        {/* This page is the "+" menu's single "Mis máquinas" destination (see
            MobileBottomNav's own doc comment) -- same "Mis X" convention as
            operators/accessories/insurance/transport vehicles: a persistent
            "add new" button up top once there's at least one item, plus a
            separate empty-state CTA below when there are none. "Ver flota en
            vivo" is this page's own addition on top of that convention --
            the fastest way into "Mi flota en vivo" from the machine list you
            were already looking at. Shown once there's at least one machine
            to show live data for. */}
        {!loading && spaces.length > 0 && (
          <PageHeaderRow
            subtitle={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_PAGE_SUBTITLE)}
            actions={
              <>
                <Button shape="round" icon={<IconEngine size={16} />} onClick={() => navigate('/heavy-duty-machinery/fleet')}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_NAV_LABEL)}
                </Button>
                <Button type="primary" shape="round" icon={<PlusOutlined />} onClick={() => navigate('/heavy-duty-machinery/new')}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_CREATE_SPACE)}
                </Button>
              </>
            }
          />
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : loadError ? (
          <EmptyState
            icon={<IconAlertTriangle size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_LOAD_ERROR)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_LOAD_ERROR_DESCRIPTION)}
            action={
              <Button shape="round" onClick={load}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_RETRY_BUTTON)}
              </Button>
            }
          />
        ) : spaces.length === 0 ? (
          isMobile ? (
            <MySpacesEmptyStateMobile
              title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_EMPTY)}
              description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_EMPTY_DESCRIPTION)}
              buttonLabel={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_CREATE_SPACE)}
              onCreate={() => navigate('/heavy-duty-machinery/new')}
            />
          ) : (
            <EmptyState
              icon={<IconHomeOff size={24} />}
              title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_EMPTY)}
              description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_EMPTY_DESCRIPTION)}
              action={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/heavy-duty-machinery/new')}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_CREATE_SPACE)}
                </Button>
              }
            />
          )
        ) : (
          <Row gutter={[16, 16]}>
            {spaces.map((space) => (
              <Col xs={24} sm={12} md={8} key={space.id}>
                <HeavyDutyMachineryCard
                  space={space}
                  onClick={() => navigate(`/heavy-duty-machinery/${space.id}`)}
                  onEdit={() => navigate(`/heavy-duty-machinery/${space.id}/edit`)}
                  onManageAvailability={() => navigate(`/heavy-duty-machinery/${space.id}/availability`)}
                  onPublish={() => handlePublish(space.id)}
                  onTakeOutOfService={() => setParkingSpace({ id: space.id, title: space.title })}
                  onBackInService={() => handleBackInService(space.id)}
                  onDelete={() => handleDelete(space.id)}
                />
              </Col>
            ))}
          </Row>
        )}
      </div>

      {parkingSpace && (
        <MachineryOutOfServiceModal
          open
          machineryId={parkingSpace.id}
          machineryTitle={parkingSpace.title}
          onClose={() => setParkingSpace(undefined)}
          onParked={(updated, affectedBookings) => {
            setSpaces((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
            setAffected(affectedBookings);
          }}
        />
      )}

      <Modal
        open={!!affected}
        onCancel={() => setAffected(undefined)}
        footer={null}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_AFFECTED_TITLE)}
      >
        {affected && affected.length === 0 ? (
          <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_AFFECTED_NONE)}</Text>
        ) : (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 14 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_AFFECTED_DESCRIPTION)}
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(affected ?? []).map((booking) => (
                <div
                  key={booking.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    borderRadius: 14,
                    background: token.colorFillQuaternary,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <Text strong style={{ display: 'block', fontSize: 14 }}>
                      {dayjs(booking.startAt).format('D MMM')} – {dayjs(booking.endAt).format('D MMM')}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {booking.reference}
                      {booking.guestDisplayName ? ` · ${booking.guestDisplayName}` : ''}
                    </Text>
                  </div>
                  <Button
                    shape="round"
                    size="small"
                    onClick={() => navigate(`/heavy-duty-machinery/bookings/${booking.id}`)}
                  >
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_AFFECTED_OPEN)}
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>
    </AppLayout>
  );
};

export default MyHeavyDutyMachineries;
